import React from "react";
import { useLocation, matchPath } from "react-router-dom";

import { SLIDES_BY_PATH, DEFAULT_SLIDE as ROUTE_DEFAULT_SLIDE } from "../../config/learnMoreSlides";

export const HowToWorkContext = React.createContext(null);

export function useHowToWork() {
  const ctx = React.useContext(HowToWorkContext);
  if (!ctx) throw new Error("useHowToWork must be used within HowToWorkProvider");
  return ctx;
}

export function useHowToWorkOptional() {
  return React.useContext(HowToWorkContext);
}

// Legacy default kept for backwards compatibility with any caller that
// still explicitly passes a `slide` prop. The panel now reads from the
// per-route SLIDES_BY_PATH mapping (see `../../config/learnMoreSlides`)
// unless the parent overrides.
const LEGACY_DEFAULT_SLIDE = ROUTE_DEFAULT_SLIDE;

/**
 * Walk SLIDES_BY_PATH in order and return the first slide whose pattern
 * matches the current pathname. Uses react-router's `matchPath` so
 * patterns can include `*` wildcards for sub-route inheritance.
 */
function pickSlide(pathname) {
  for (const entry of SLIDES_BY_PATH) {
    if (matchPath({ path: entry.pattern, end: true }, pathname)) {
      return entry.slide;
    }
  }
  return null;
}

export function HowToWorkProvider({ children, slide: slideOverride }) {
  const { pathname } = useLocation();
  const [hideFlag, setHideFlag] = React.useState(
    () => window.localStorage.getItem("hideHowItWorks") === "true"
  );
  const [videoModalOpen, setVideoModalOpen] = React.useState(false);

  // Precedence:
  //   1. explicit `slide` prop (a page can still override, though nobody
  //      currently does — this is what LEGACY_DEFAULT_SLIDE covers)
  //   2. route-driven slide from SLIDES_BY_PATH
  //   3. ROUTE_DEFAULT_SLIDE (generic fallback pointing at docs.layerd.com)
  const slide = React.useMemo(() => {
    if (slideOverride) return slideOverride;
    return pickSlide(pathname) || LEGACY_DEFAULT_SLIDE;
  }, [slideOverride, pathname]);

  const toggle = React.useCallback(() => {
    setHideFlag((prev) => {
      const next = !prev;
      window.localStorage.setItem("hideHowItWorks", next ? "true" : "false");
      return next;
    });
  }, []);

  const expand = React.useCallback(() => {
    setHideFlag(false);
    window.localStorage.setItem("hideHowItWorks", "false");
  }, []);

  const value = React.useMemo(() => ({
    hideFlag,
    setHideFlag,
    toggle,
    expand,
    videoModalOpen,
    setVideoModalOpen,
    slide,
  }), [hideFlag, toggle, expand, videoModalOpen, slide]);

  return (
    <HowToWorkContext.Provider value={value}>
      {children}
    </HowToWorkContext.Provider>
  );
}
